import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type Inspecao, type FotoInspecao } from "../api";
import type { Row } from "../types";
import { CHECKLIST_VEICULO, INSPECAO_RESPOSTAS } from "../config";
import ErrorState from "./ErrorState";
import { formatDate } from "../format";
import { IconClose, IconCamera, IconTrash } from "../icons";
import { prepararFoto, FotoInvalida } from "../foto";
import { ordenarPorNome } from "../ordem";

/** Texto do veículo no seletor — é também a chave da ordem alfabética. */
const rotuloVeiculo = (v: Row) =>
  [v.placa, v.modelo].filter(Boolean).join(" — ") || `Veículo ${v.id}`;

/** Máximo de fotos por item — o mesmo limite validado no servidor. */
const MAX_FOTOS_ITEM = 5;

/** Estado editável de uma resposta do checklist. */
interface RespEdit {
  item: string;
  resposta: string;
  obs: string;
  /** Fotos do item, já reduzidas, como data URL. */
  fotos: string[];
}

/** Cria o checklist zerado (todas "Conformidade" por padrão — o inspetor sinaliza exceções). */
function checklistInicial(): RespEdit[] {
  return CHECKLIST_VEICULO.map((item) => ({
    item,
    resposta: INSPECAO_RESPOSTAS[0],
    obs: "",
    fotos: [],
  }));
}

function hojeYmd(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Pílula de status da inspeção. */
const STATUS_PILL: Record<string, string> = {
  Conforme: "pill-ok",
  "Com pendências": "pill-danger",
};

/**
 * Inspeção do Veículo (Operacional). Checklist pré-uso: cada item é uma
 * "pergunta de selecionar" (Conforme / Não conforme / N/A). Ao enviar, o
 * servidor calcula o status (qualquer "Não conforme" => "Com pendências").
 * Abaixo, o histórico das inspeções (o colaborador vê as suas; o admin vê todas).
 *
 * `somenteLeitura` (cadastro do Portal): o checklist aparece, mas travado — sem
 * digitar, sem foto e sem enviar; o histórico segue consultável.
 */
export default function InspecaoVeiculoView({ somenteLeitura = false }: { somenteLeitura?: boolean }) {
  const [veiculos, setVeiculos] = useState<Row[]>([]);
  const [rows, setRows] = useState<Inspecao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Formulário
  const [veiculoId, setVeiculoId] = useState<string>("");
  const [placa, setPlaca] = useState("");
  const [modelo, setModelo] = useState("");
  const [km, setKm] = useState("");
  const [data, setData] = useState(hojeYmd());
  const [respostas, setRespostas] = useState<RespEdit[]>(checklistInicial);
  const [observacoes, setObservacoes] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  /** Quantas fotos estão sendo reduzidas agora (trava o envio até terminar). */
  const [preparando, setPreparando] = useState(0);
  /** Foto aberta em tamanho grande (toque na miniatura). */
  const [zoom, setZoom] = useState<string | null>(null);

  const [detalhe, setDetalhe] = useState<Inspecao | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [insp, veic] = await Promise.all([
        api.inspecoes.list(),
        api.list("veiculos").catch(() => [] as Row[]),
      ]);
      setRows(insp);
      setVeiculos(veic);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Ao escolher um veículo cadastrado, pré-preenche placa/modelo/km.
  function escolherVeiculo(id: string) {
    setVeiculoId(id);
    const v = veiculos.find((x) => String(x.id) === id);
    if (v) {
      setPlaca(String(v.placa ?? ""));
      setModelo(String(v.modelo ?? ""));
      setKm(v.km_atual != null ? String(v.km_atual) : "");
    } else {
      // Voltou para "Avulso": limpa o que foi pré-preenchido do veículo anterior.
      setPlaca("");
      setModelo("");
      setKm("");
    }
  }

  function setResposta(i: number, campo: "resposta" | "obs", valor: string) {
    setRespostas((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: valor } : r)));
  }

  /**
   * Fotos do item: reduz cada arquivo no próprio aparelho antes de guardar no
   * estado (foto de celular tem 3–5 MB; vai para ~200 KB). Erros são mostrados
   * na tela — a pessoa tirou a foto e precisa saber se ela não entrou.
   */
  async function adicionarFotos(i: number, arquivos: FileList | null) {
    if (!arquivos || arquivos.length === 0) return;
    setFormError(null);
    const atuais = respostas[i]?.fotos.length ?? 0;
    const cabem = MAX_FOTOS_ITEM - atuais;
    if (cabem <= 0) {
      setFormError(`Cada item aceita no máximo ${MAX_FOTOS_ITEM} fotos.`);
      return;
    }
    const escolhidos = Array.from(arquivos).slice(0, cabem);
    if (escolhidos.length < arquivos.length) {
      setFormError(`Só couberam ${escolhidos.length} foto(s): o limite é ${MAX_FOTOS_ITEM} por item.`);
    }
    setPreparando((n) => n + 1);
    try {
      const prontas: string[] = [];
      for (const arq of escolhidos) {
        try {
          prontas.push(await prepararFoto(arq));
        } catch (err) {
          setFormError(err instanceof FotoInvalida ? err.message : `Falha ao ler "${arq.name}".`);
        }
      }
      if (prontas.length > 0) {
        setRespostas((prev) =>
          prev.map((r, idx) => (idx === i ? { ...r, fotos: [...r.fotos, ...prontas] } : r)),
        );
      }
    } finally {
      setPreparando((n) => n - 1);
    }
  }

  function removerFoto(i: number, pos: number) {
    setRespostas((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, fotos: r.fotos.filter((_, p) => p !== pos) } : r)),
    );
  }

  const pendencias = useMemo(
    () => respostas.filter((r) => r.resposta === "Não conformidade").length,
    [respostas],
  );

  function limpar() {
    setVeiculoId("");
    setPlaca("");
    setModelo("");
    setKm("");
    setData(hojeYmd());
    setRespostas(checklistInicial());
    setObservacoes("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setOk(null);
    if (!placa.trim() && !veiculoId) {
      setFormError("Selecione um veículo ou informe a placa.");
      return;
    }
    // Registro complementar é obrigatório em cada item marcado como "Não conformidade".
    if (respostas.some((r) => r.resposta === "Não conformidade" && !r.obs.trim())) {
      setFormError('Preencha o "registro complementar" em cada item marcado como Não conformidade.');
      return;
    }
    setSaving(true);
    try {
      const totalFotos = respostas.reduce((s, r) => s + r.fotos.length, 0);
      const res = await api.inspecoes.create({
        veiculo_id: veiculoId ? Number(veiculoId) : null,
        placa: placa.trim(),
        modelo: modelo.trim(),
        km: km === "" ? null : Number(km),
        data,
        respostas: respostas.map((r) => ({
          item: r.item,
          resposta: r.resposta,
          obs: r.obs.trim(),
          fotos: r.fotos,
        })),
        observacoes: observacoes.trim(),
      });
      setOk(
        `Inspeção registrada (${res.status})` +
          (totalFotos > 0 ? ` com ${totalFotos} foto(s).` : "."),
      );
      limpar();
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Erro ao registrar a inspeção.");
    } finally {
      setSaving(false);
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud inspecao">
      <form className="inspecao-form form-card" onSubmit={submit}>
        <fieldset className="form-travado" disabled={somenteLeitura}>
          <div className="form-grid">
            <label className="field">
              <span>Veículo cadastrado</span>
              <select value={veiculoId} onChange={(e) => escolherVeiculo(e.target.value)}>
                <option value="">Avulso (informar placa)</option>
                {/* Em ordem alfabética pelo que aparece na opção (placa — modelo). */}
                {ordenarPorNome(veiculos, rotuloVeiculo).map((v) => (
                  <option key={v.id} value={v.id}>
                    {rotuloVeiculo(v)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Placa</span>
              <input type="text" value={placa} onChange={(e) => setPlaca(e.target.value)} placeholder="ABC-1D23" />
            </label>
            <label className="field">
              <span>Modelo</span>
              <input type="text" value={modelo} onChange={(e) => setModelo(e.target.value)} />
            </label>
            <label className="field">
              <span>KM atual</span>
              <input type="number" value={km} onChange={(e) => setKm(e.target.value)} min="0" />
            </label>
            <label className="field">
              <span>Data</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
          </div>

          <div className="inspecao-checklist">
            <div className="inspecao-check-head">
              <h3 className="section-title">Checklist de inspeção</h3>
              <span className={`pill ${pendencias > 0 ? "pill-danger" : "pill-ok"}`}>
                {pendencias > 0 ? `${pendencias} não conforme(s)` : "Sem pendências"}
              </span>
            </div>
            <div className="inspecao-items">
              {respostas.map((r, i) => (
                <div key={r.item} className={`inspecao-item${r.resposta === "Não conformidade" ? " is-nc" : ""}`}>
                  <span className="inspecao-item-label">{r.item}</span>
                  <select
                    className="inspecao-item-select"
                    value={r.resposta}
                    onChange={(e) => setResposta(i, "resposta", e.target.value)}
                  >
                    {INSPECAO_RESPOSTAS.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                  <input
                    className="inspecao-item-obs"
                    type="text"
                    value={r.obs}
                    placeholder="Registro complementar — preencher em caso de não conformidade"
                    onChange={(e) => setResposta(i, "obs", e.target.value)}
                  />
                  {/* Foto DESTE item do checklist. No celular abre a câmera direto;
                      no computador abre a seleção de arquivo. */}
                  <FotosDoItem
                    fotos={r.fotos}
                    indice={i}
                    onAdicionar={(arquivos) => adicionarFotos(i, arquivos)}
                    onRemover={(pos) => removerFoto(i, pos)}
                    onAmpliar={setZoom}
                  />
                </div>
              ))}
            </div>
          </div>

          <label className="field field-wide">
            <span>Observações gerais</span>
            <textarea rows={3} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
          </label>

          {formError && <div className="form-error">{formError}</div>}
          {ok && <div className="form-ok">{ok}</div>}

          <div className="inspecao-foot">
            {preparando > 0 && <span className="muted">Preparando foto…</span>}
            <button type="button" className="btn-secondary" onClick={limpar} disabled={saving}>
              Limpar
            </button>
            {/* Trava enquanto alguma foto ainda está sendo reduzida, senão ela
                ficaria de fora do envio sem ninguém perceber. */}
            <button type="submit" className="btn-primary" disabled={saving || preparando > 0}>
              {saving ? "Registrando..." : "Registrar inspeção"}
            </button>
          </div>
        </fieldset>
      </form>

      <h3 className="section-title">Histórico de inspeções</h3>
      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="empty">Nenhuma inspeção registrada ainda.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Veículo</th>
                <th>KM</th>
                <th>Status</th>
                <th>Inspetor</th>
                <th className="col-actions">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const veic = [r.placa || r.veiculo_placa, r.modelo || r.veiculo_modelo]
                  .filter(Boolean)
                  .join(" — ");
                return (
                  <tr key={r.id}>
                    <td>{formatDate(r.data)}</td>
                    <td>{veic || "—"}</td>
                    <td>{r.km ?? "—"}</td>
                    <td>
                      <span className={`pill ${STATUS_PILL[r.status] ?? "pill-neutral"}`}>{r.status}</span>
                    </td>
                    <td>{r.inspetor ?? "—"}</td>
                    <td className="col-actions">
                      <button className="btn-secondary btn-sm" onClick={() => setDetalhe(r)}>
                        Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && (
        <DetalheModal inspecao={detalhe} onClose={() => setDetalhe(null)} onAmpliar={setZoom} />
      )}
      {zoom && <FotoAmpliada url={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

/* --------------------- Fotos de um item do checklist --------------------- */
function FotosDoItem({
  fotos,
  indice,
  onAdicionar,
  onRemover,
  onAmpliar,
}: {
  fotos: string[];
  indice: number;
  onAdicionar: (arquivos: FileList | null) => void;
  onRemover: (pos: number) => void;
  onAmpliar: (url: string) => void;
}) {
  // Dois inputs: um abre a CÂMERA (celular/tablet) e outro a galeria/arquivos.
  // No computador o `capture` é ignorado, então lá os dois abrem o seletor —
  // por isso o segundo botão existe: garante o caminho em qualquer aparelho.
  const camera = useRef<HTMLInputElement>(null);
  const arquivo = useRef<HTMLInputElement>(null);
  const cheio = fotos.length >= MAX_FOTOS_ITEM;

  return (
    <div className="inspecao-item-fotos">
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden-input"
        onChange={(e) => {
          onAdicionar(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={arquivo}
        type="file"
        accept="image/*"
        multiple
        className="hidden-input"
        onChange={(e) => {
          onAdicionar(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="inspecao-foto-acoes">
        <button
          type="button"
          className="btn-foto"
          onClick={() => camera.current?.click()}
          disabled={cheio}
          title={cheio ? `Máximo de ${MAX_FOTOS_ITEM} fotos` : "Tirar foto deste item"}
        >
          <IconCamera size={15} /> Tirar foto
        </button>
        <button
          type="button"
          className="btn-foto btn-foto-ghost"
          onClick={() => arquivo.current?.click()}
          disabled={cheio}
          title={cheio ? `Máximo de ${MAX_FOTOS_ITEM} fotos` : "Escolher imagem já salva"}
        >
          Anexar
        </button>
        {fotos.length > 0 && (
          <span className="inspecao-foto-contador">
            {fotos.length}/{MAX_FOTOS_ITEM}
          </span>
        )}
      </div>

      {fotos.length > 0 && (
        <ul className="inspecao-foto-lista">
          {fotos.map((url, pos) => (
            <li key={`${indice}-${pos}`} className="inspecao-foto-thumb">
              <button type="button" className="inspecao-foto-abrir" onClick={() => onAmpliar(url)}>
                <img src={url} alt={`Foto ${pos + 1}`} />
              </button>
              {/* Sempre visível — nada de aparecer só quando o mouse passa por
                  cima: em celular e tablet não existe isso. */}
              <button
                type="button"
                className="inspecao-foto-remover"
                onClick={() => onRemover(pos)}
                title="Apagar esta foto"
                aria-label={`Apagar a foto ${pos + 1}`}
              >
                <IconTrash size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------- Foto em tamanho grande ------------------------- */
function FotoAmpliada({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay foto-zoom" onMouseDown={onClose}>
      <img src={url} alt="Foto da inspeção" onMouseDown={(e) => e.stopPropagation()} />
      <button type="button" className="icon-btn foto-zoom-fechar" onClick={onClose}>
        <IconClose size={20} />
      </button>
    </div>
  );
}

/* ----------------------------- Modal de detalhes ----------------------------- */
function DetalheModal({
  inspecao,
  onClose,
  onAmpliar,
}: {
  inspecao: Inspecao;
  onClose: () => void;
  onAmpliar: (url: string) => void;
}) {
  const veic = [inspecao.placa || inspecao.veiculo_placa, inspecao.modelo || inspecao.veiculo_modelo]
    .filter(Boolean)
    .join(" — ");
  const respostas = Array.isArray(inspecao.respostas) ? inspecao.respostas : [];

  // As imagens não vêm na listagem (seriam megabytes por inspeção): buscamos
  // aqui, só quando alguém abre os detalhes.
  const [fotos, setFotos] = useState<FotoInspecao[]>([]);
  const [carregandoFotos, setCarregandoFotos] = useState(false);
  const [erroFotos, setErroFotos] = useState<string | null>(null);

  useEffect(() => {
    if (!inspecao.fotos_total) return;
    let vivo = true;
    setCarregandoFotos(true);
    api.inspecoes
      .fotos(inspecao.id)
      .then((f) => {
        if (vivo) setFotos(f);
      })
      .catch((err) => {
        if (vivo) setErroFotos(err instanceof ApiError ? err.message : "Falha ao carregar as fotos.");
      })
      .finally(() => {
        if (vivo) setCarregandoFotos(false);
      });
    return () => {
      vivo = false;
    };
  }, [inspecao.id, inspecao.fotos_total]);

  /** Fotos de um item, casadas pela posição (e pelo texto, para registros antigos). */
  const fotosDoItem = (indice: number, item: string) =>
    fotos.filter((f) => f.item_index === indice || (f.item && f.item === item));

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Inspeção — {veic || "veículo avulso"}</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="inspecao-modal-meta">
            <span>{formatDate(inspecao.data)}</span>
            {inspecao.km != null && <span>KM {inspecao.km}</span>}
            <span className={`pill ${STATUS_PILL[inspecao.status] ?? "pill-neutral"}`}>{inspecao.status}</span>
            {inspecao.inspetor && <span className="muted">por {inspecao.inspetor}</span>}
          </div>
          {carregandoFotos && <p className="muted">Carregando fotos…</p>}
          {erroFotos && <div className="form-error">{erroFotos}</div>}

          <table className="table table-compact">
            <thead>
              <tr>
                <th>Item</th>
                <th>Resposta</th>
                <th>Observação</th>
                <th>Fotos</th>
              </tr>
            </thead>
            <tbody>
              {respostas.map((r, i) => {
                const doItem = fotosDoItem(i, r.item);
                return (
                  <tr key={i} className={r.resposta === "Não conformidade" ? "is-nc-row" : ""}>
                    <td>{r.item}</td>
                    <td>{r.resposta}</td>
                    <td>{r.obs || "—"}</td>
                    <td>
                      {doItem.length === 0 ? (
                        "—"
                      ) : (
                        <ul className="inspecao-foto-lista compacta">
                          {doItem.map((f) => (
                            <li key={f.id} className="inspecao-foto-thumb">
                              <button
                                type="button"
                                className="inspecao-foto-abrir"
                                onClick={() => onAmpliar(f.url)}
                              >
                                <img src={f.url} alt={`Foto de ${r.item}`} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {inspecao.observacoes && (
            <p className="inspecao-modal-obs">
              <strong>Observações gerais:</strong> {inspecao.observacoes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
